import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { MCPToolsService } from '@/api/services/mcpToolsService';
import { MCPToolCard } from './MCPToolCard';
import { CreateMCPToolDialog } from './CreateMCPToolDialog';
import { MCPToolDetailView } from './MCPToolDetailView';
import { CreateMCPActionDialogNew as CreateMCPActionDialog } from './CreateMCPActionDialogNew';
import { MCPTool } from '@/types/mcpTool';
import {
  Loader2,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Plus,
  Box,
  Shield,
  Wrench,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const MCPToolsTab: React.FC = () => {
  const navigate = useNavigate();
  const [editingTool, setEditingTool] = useState<MCPTool | null>(null);
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [authFilter, setAuthFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<MCPTool | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null);
  const [createActionDialogOpen, setCreateActionDialogOpen] = useState(false);
  const [actionToolContext, setActionToolContext] = useState<MCPTool | null>(null);
  const [actionToEdit, setActionToEdit] = useState<any | null>(null);
  const [actionDialogMode, setActionDialogMode] = useState<'create' | 'edit'>('create');

  const fetchMCPTools = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MCPToolsService.fetchMCPTools({
        limit: 100,
      });

      setTools(response.tools || []);
    } catch (err: any) {
      console.error('Error fetching MCP tools:', err);
      setError(err.message || 'Failed to load MCP tools. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMCPTools();
  }, []);

  // Filter tools based on search and auth filter
  const filteredTools = tools.filter((tool) => {
    const matchesSearch =
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.base_url || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAuthFilter =
      authFilter === 'all' ||
      (authFilter === 'configured' && tool.auth_configured) ||
      (authFilter === 'oauth' && tool.requires_user_oauth) ||
      (authFilter === 'none' && !tool.auth_configured);

    return matchesSearch && matchesAuthFilter;
  });

  const handleToolClick = (tool: MCPTool) => {
    setSelectedTool(tool);
    setDetailViewOpen(true);
  };

  const handleCreateTool = () => {
    setEditingTool(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    fetchMCPTools(); // Refresh the list
  };

  const handleEditTool = (tool: MCPTool) => {
    if (tool.managed) { navigate('/home/automation/ai/tools'); return; }
    setEditingTool(tool);
    setCreateDialogOpen(true);
  };

  const handleCreateAction = (tool: MCPTool) => {
    if (tool.managed) { navigate('/home/automation/ai/tools'); return; }
    setActionToolContext(tool);
    setDetailViewOpen(false);
    setActionToEdit(null);
    setActionDialogMode('create');
    setCreateActionDialogOpen(true);
  };

  const handleActionSuccess = () => {
    fetchMCPTools();
    // Refresh the detail view by closing and reopening it
    // This will trigger a fresh fetch of actions
    if (selectedTool) {
      setDetailViewOpen(false);
      setTimeout(() => {
        setDetailViewOpen(true);
      }, 100);
    }
  };

  const handleEditAction = (tool: MCPTool, action: any) => {
    if (tool.managed) { navigate('/home/automation/ai/tools'); return; }
    setActionToolContext(tool);
    setDetailViewOpen(false);
    setActionToEdit(action);
    setActionDialogMode('edit');
    setCreateActionDialogOpen(true);
  };

  const handleDeleteTool = (tool: MCPTool) => {
    if (tool.managed) { navigate('/home/automation/ai/tools'); return; }
    setToolToDelete(tool);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTool = async () => {
    if (!toolToDelete) return;

    try {
      await MCPToolsService.deleteMCPTool(toolToDelete.id!);
      setTools(tools.filter((t) => t.name !== toolToDelete.name));
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    } catch (err) {
      console.error('Error deleting tool:', err);
      setError('Failed to delete tool. Please try again.');
    }
  };

  // Stats
  const totalTools = tools.length;
  const toolsWithAuth = tools.filter((t) => t.auth_configured).length;
  const toolsWithOAuth = tools.filter((t) => t.requires_user_oauth).length;
  const totalActions = tools.reduce((sum, tool) => sum + (tool.actions?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-gray-600">Loading MCP tools...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMCPTools}
              className="ml-2"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">MCP Tools</h3>
          <div className="flex gap-2">
            <Badge variant="secondary" className="px-2 py-1">
              <Box className="h-3 w-3 mr-1" />
              {totalTools} {totalTools === 1 ? 'Tool' : 'Tools'}
            </Badge>
            <Badge
              variant="outline"
              className="px-2 py-1 text-green-700 bg-green-50 border-green-200"
            >
              <Shield className="h-3 w-3 mr-1" />
              {toolsWithAuth} With Auth
            </Badge>
            {toolsWithOAuth > 0 && (
              <Badge
                variant="outline"
                className="px-2 py-1 text-purple-700 bg-purple-50 border-purple-200"
              >
                {toolsWithOAuth} OAuth
              </Badge>
            )}
            <Badge
              variant="outline"
              className="px-2 py-1 text-blue-700 bg-blue-50 border-blue-200"
            >
              <Wrench className="h-3 w-3 mr-1" />
              {totalActions} {totalActions === 1 ? 'Action' : 'Actions'}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMCPTools}
            disabled={loading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button size="sm" onClick={handleCreateTool}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tool
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search tools by name, description, or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={authFilter} onValueChange={setAuthFilter}>
          <SelectTrigger className="w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by auth" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tools</SelectItem>
            <SelectItem value="configured">With Auth</SelectItem>
            <SelectItem value="oauth">OAuth Only</SelectItem>
            <SelectItem value="none">No Auth</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tools Grid */}
      {filteredTools.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {searchTerm || authFilter !== 'all' ? (
              <Search className="h-6 w-6 text-gray-400" />
            ) : (
              <Box className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || authFilter !== 'all'
              ? 'No tools found'
              : 'No MCP tools yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || authFilter !== 'all'
              ? 'Try adjusting your filters or search terms.'
              : 'Create your first MCP tool to integrate external APIs.'}
          </p>
          {searchTerm || authFilter !== 'all' ? (
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setAuthFilter('all');
              }}
            >
              Clear Filters
            </Button>
          ) : (
            <Button onClick={handleCreateTool}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Tool
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTools.map((tool) => (
            <MCPToolCard
              key={tool.name}
              tool={tool}
              onClick={handleToolClick}
              onEdit={handleEditTool}
              onDelete={handleDeleteTool}
            />
          ))}
        </div>
      )}

      {/* Create Tool Dialog */}
      <CreateMCPToolDialog
        initialTool={editingTool}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      {/* Tool Detail View */}
      <MCPToolDetailView
        tool={selectedTool}
        open={detailViewOpen}
        onOpenChange={setDetailViewOpen}
        onCreateAction={handleCreateAction}
        onEditAction={handleEditAction}
      />

      {/* Create/Edit Action Dialog */}
      <CreateMCPActionDialog
        tool={actionToolContext}
        open={createActionDialogOpen}
        onOpenChange={open => { setCreateActionDialogOpen(open); if (!open && selectedTool) setDetailViewOpen(true); }}
        onSuccess={handleActionSuccess}
        mode={actionDialogMode}
        initialAction={actionToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MCP Tool?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{toolToDelete?.name}</strong>?
              The tool will be removed from Action Center and its credentials cleared. Its saved action definitions will be retained.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setToolToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTool}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Tool
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
